import { useState, useEffect } from 'react';
import {
  Box, TextField, Typography, Button, Alert,
  IconButton, InputAdornment, CircularProgress, Collapse,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset, CheckCircle } from '@mui/icons-material';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

const PasswordChange = () => {
  useSessionValidation();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, userId, logout, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); }
  }, [isAuthenticated]);

  const validatePassword = () => {
    if (newPassword.length < 8) { setErrorMessage('Password must be at least 8 characters long.'); return false; }
    if (!/[A-Z]/.test(newPassword)) { setErrorMessage('Password must contain at least one uppercase letter.'); return false; }
    if (!/[a-z]/.test(newPassword)) { setErrorMessage('Password must contain at least one lowercase letter.'); return false; }
    if (!/\d/.test(newPassword)) { setErrorMessage('Password must contain at least one digit.'); return false; }
    if (newPassword !== confirmPassword) { setErrorMessage('Passwords do not match.'); return false; }
    setErrorMessage('');
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    setLoading(true);
    try {
      const response = await updateData('UserTokenApi', `${userId}`, {
        CurrentPassword: oldPassword, NewPassword: newPassword,
        ConfirmPassword: confirmPassword, UserId: userId,
      }, token);
      setSuccessMessage(response.message || 'Password changed successfully.');
      setErrorMessage('');
    } catch {
      setSuccessMessage('');
      setErrorMessage('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#f8f9fa',
      fontSize: '13px',
      '&:hover fieldset': { borderColor: '#2C3E6E' },
      '&.Mui-focused fieldset': { borderColor: '#2C3E6E', borderWidth: 1 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E6E' },
  };

  const passwordProps = {
    type: showPassword ? 'text' : 'password',
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', backgroundColor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden', maxWidth: '420px', width: '90vw' }}>

        {/* Header */}
        <Box sx={{ background: '#2C3E6E', p: '10px 16px', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockReset sx={{ fontSize: 20, color: '#fff' }} />
          <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'Times New Roman' }}>
            Change Password
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Alerts */}
          <Collapse in={!!successMessage || !!errorMessage}>
            <Box sx={{ mb: 1.5 }}>
              {successMessage && <Alert severity="success" sx={{ borderRadius: '8px', fontSize: '13px' }}>{successMessage}</Alert>}
              {errorMessage && <Alert severity="error" sx={{ borderRadius: '8px', fontSize: '13px' }}>{errorMessage}</Alert>}
            </Box>
          </Collapse>

          <TextField fullWidth label="Current Password" variant="outlined" size="small" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} sx={fieldSx} />
          <TextField fullWidth label="New Password" variant="outlined" size="small" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} {...passwordProps} sx={fieldSx} />
          <TextField fullWidth label="Confirm New Password" variant="outlined" size="small" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} {...passwordProps} sx={fieldSx} />

          {/* Password Requirements */}
          <Collapse in={newPassword.length > 0}>
            <Box sx={{ mb: 2, p: 1.5, background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {[
                  { test: newPassword.length >= 8, label: '8+ chars' },
                  { test: /[A-Z]/.test(newPassword), label: 'Uppercase' },
                  { test: /[a-z]/.test(newPassword), label: 'Lowercase' },
                  { test: /\d/.test(newPassword), label: 'Number' },
                ].map((req, idx) => (
                  <Typography key={idx} variant="caption" sx={{ color: req.test ? '#2C3E6E' : '#9ca3af', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: '50%', backgroundColor: req.test ? '#2C3E6E' : '#e2e8f0', color: '#fff', fontSize: 10, fontWeight: 700 }}>
                      {req.test ? '✓' : ''}
                    </Box>
                    {req.label}
                  </Typography>
                ))}
              </Box>
              {confirmPassword && (
                <Typography variant="caption" sx={{ color: newPassword === confirmPassword ? '#2C3E6E' : '#ef4444', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500, mt: 0.5 }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: '50%', backgroundColor: newPassword === confirmPassword ? '#2C3E6E' : '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700 }}>
                    {newPassword === confirmPassword ? '✓' : '✕'}
                  </Box>
                  Passwords {newPassword === confirmPassword ? 'match' : 'do not match'}
                </Typography>
              )}
            </Box>
          </Collapse>

          <Button
            variant="contained"
            fullWidth
            disabled={loading}
            onClick={handleChangePassword}
            sx={{
              py: 1, fontWeight: 700, fontSize: '13px', borderRadius: '8px',
              background: '#2C3E6E', textTransform: 'none',
              '&:hover': { background: '#1e2d52' },
              '&.Mui-disabled': { background: '#e9ecef', color: '#6c757d' },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : <><LockReset sx={{ mr: 1, fontSize: 18 }} />Update Password</>}
          </Button>

          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#9ca3af', mt: 1.5, fontWeight: 500, fontSize: '0.7rem' }}>
            © EFFTABS SOLUTIONS LLP
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PasswordChange;
